import React, { useState } from 'react';
import { X, Mail, Building2, Send, Loader2, CheckCircle } from 'lucide-react';
import { sendCollabEmails } from '../api/api';

export default function CollaboratorInviteModal({ activityId, collaborators = [], onClose }) {
  const [emails, setEmails] = useState(
    collaborators.reduce((acc, c) => ({ ...acc, [c.id]: c.invited_email || '' }), {})
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  if (!collaborators.length) return null;

  const handleSend = async () => {
    const entries = collaborators
      .map(c => ({ collab_id: c.id, email: (emails[c.id] || '').trim() }))
      .filter(e => e.email && e.email.includes('@'));

    if (!entries.length) {
      setError('Please enter at least one email address.');
      return;
    }

    setSending(true);
    setError('');
    try {
      await sendCollabEmails(activityId, { entries });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send emails. You can retry later.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="collab-invite-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Invite Collaborators</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Add contact emails so collaborating organisations are notified.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="collab-invite-close">
            <X size={16} />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center space-y-3">
            <CheckCircle size={36} className="text-green-500 mx-auto" />
            <p className="font-medium text-gray-800">Invites sent!</p>
            <p className="text-sm text-gray-500">Collaboration emails have been sent to the provided addresses.</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              data-testid="collab-invite-done-btn"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {collaborators.map(collab => (
              <div key={collab.id} className="space-y-1.5" data-testid={`collab-invite-row-${collab.id}`}>
                <div className="flex items-center gap-2">
                  {collab.org_logo_url
                    ? <img src={collab.org_logo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                    : <Building2 size={14} className="text-gray-300" />}
                  <span className="text-sm font-medium text-gray-800 truncate">{collab.org_name}</span>
                </div>
                <div className="flex items-center gap-2 pl-1">
                  <Mail size={12} className="text-gray-400 flex-shrink-0" />
                  <input
                    type="email"
                    value={emails[collab.id] || ''}
                    onChange={e => setEmails(prev => ({ ...prev, [collab.id]: e.target.value }))}
                    placeholder="Contact email"
                    className="flex-1 text-sm px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500"
                    data-testid={`collab-invite-email-${collab.id}`}
                  />
                </div>
              </div>
            ))}

            {error && <p className="text-xs text-red-500" data-testid="collab-invite-error">{error}</p>}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="collab-invite-skip"
              >
                Do this later
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors"
                data-testid="collab-invite-send-btn"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Invite & Collaborate
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
